import {
  AppAccessCreatePackage,
  AppAccessPackage,
  AppAccessTarget,
  useCreateAppAccessPackageMutation,
  useEmailAppAccessPackageMutation,
  useSmsAppAccessPackageMutation,
  useGetAppAccessCapabilitiesQuery,
  useGetAppAccessIdentitiesQuery,
  useGetAppAccessDeliveryAuditsQuery,
  useGetAppAccessPackagesQuery,
  useLazyGetAppAccessPdfQuery,
  useLazyGetAppAccessQrQuery,
  useGetAppAccessLinkMutation,
  useRevokeAppAccessPackageMutation,
  useRevokeAppAccessTargetMutation,
} from "@ots-frontend/ots-data";
import {ArrowPathIcon, PlusIcon} from "@heroicons/react/20/solid";
import {skipToken} from "@reduxjs/toolkit/query";
import {useEffect, useMemo, useState} from "react";
import {useApp} from "../app/AppContext";
import {useAuth} from "../app/AuthProvider";
import {AdminPage} from "../components/AdminPage";
import {OtsActionButton} from "../components/OtsButton";
import {OtsInlineAlert} from "../components/OtsInlineAlert";
import {ConfirmationDialog} from "../dialogs/ConfirmationDialog";
import {OtsBrand, OtsColorSchemePreference, OtsThemeRoot} from "../theme/OtsTheme";
import {getAppAccessAppLabel} from "./app-access-ui";
import {AppAccessPackageDetails} from "./AppAccessPackageDetails";
import {AppAccessPackageForm, AppAccessPackageFormValue} from "./AppAccessPackageForm";
import {AppAccessPackageList} from "./AppAccessPackageList";
import {AppAccessQuickStart} from "./AppAccessQuickStart";
import {AppAccessWorkflow, AppAccessWorkflowTabs} from "./AppAccessWorkflowTabs";

const errorMessage = (error: unknown): string => {
  const value = error as {data?: {message?: string; detail?: string}; message?: string};
  return value?.data?.detail ?? value?.data?.message ?? value?.message ?? "The request could not be completed.";
};

const downloadBlob = (blob: Blob, fileName: string) => {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = fileName;
  anchor.click();
  URL.revokeObjectURL(url);
};

/** API-aware App Access page; presentation and local form concerns live in reusable child components. */
export type AppAccessManagementPageProps = {
  brand?: OtsBrand;
  colorScheme?: OtsColorSchemePreference;
  onAction?: (event: AppAccessActionEvent) => void;
};

export type AppAccessActionEvent = {
  type: "CREATED" | "LINK_COPIED" | "QR_EXPORTED" | "PDF_EXPORTED" | "EMAIL_SENT" | "SMS_SENT" | "PACKAGE_REVOKED" | "TARGET_REVOKED" | "REFRESHED";
  message: string;
};

type PendingRevocation =
  | {kind: "PACKAGE"; appAccessPackage: AppAccessPackage}
  | {kind: "TARGET"; target: AppAccessTarget};

export const AppAccessManagementPage = ({brand, colorScheme, onAction}: AppAccessManagementPageProps) => {
  const {regionId, regionName} = useApp();
  const {hasAccess} = useAuth();
  const canView = hasAccess("app_access_view");
  const canManage = hasAccess("app_access_manage");
  const canDistributeCredentials = hasAccess("app_access_distribute_credentials");
  const canSendEmail = hasAccess("app_access_send_email");
  const canSendSms = hasAccess("app_access_send_sms");
  const {data: packages = [], isLoading, isFetching, error: listError, refetch: refetchPackages} = useGetAppAccessPackagesQuery(
    canView && regionId ? {limit: 50, regionId} : skipToken,
  );
  const {data: capabilities, refetch: refetchCapabilities} = useGetAppAccessCapabilitiesQuery(undefined, {skip: !canView});
  const {data: appIdentities = [], refetch: refetchIdentities} = useGetAppAccessIdentitiesQuery(regionId ?? skipToken, {skip: !canManage});
  const [createPackage, createState] = useCreateAppAccessPackageMutation();
  const [revokePackage, revokePackageState] = useRevokeAppAccessPackageMutation();
  const [revokeTarget, revokeTargetState] = useRevokeAppAccessTargetMutation();
  const [emailPackage, emailState] = useEmailAppAccessPackageMutation();
  const [smsPackage, smsState] = useSmsAppAccessPackageMutation();
  const [getLink] = useGetAppAccessLinkMutation();
  const [getPdf] = useLazyGetAppAccessPdfQuery();
  const [getQr] = useLazyGetAppAccessQrQuery();

  const [workflow, setWorkflow] = useState<AppAccessWorkflow>("REUSABLE");
  const [selected, setSelected] = useState<AppAccessPackage>();
  const [showCreate, setShowCreate] = useState(false);
  const [draft, setDraft] = useState<{key: number; mode: AppAccessPackage["mode"]; apps: AppAccessTarget["app"][]}>({
    key: 0,
    mode: "ONE_TIME_CREDENTIAL",
    apps: ["FIELD_GO"],
  });
  const [error, setError] = useState<string>();
  const [notice, setNotice] = useState<string>();
  const [pendingRevocation, setPendingRevocation] = useState<PendingRevocation>();
  const onePersonPackages = useMemo(
    () => packages.filter((appAccessPackage) => appAccessPackage.mode === "ONE_TIME_CREDENTIAL"),
    [packages],
  );
  const reusablePackages = useMemo(
    () => packages.filter((appAccessPackage) => appAccessPackage.mode === "CONFIGURATION"),
    [packages],
  );
  const workflowPackages = workflow === "ONE_PERSON" ? onePersonPackages : reusablePackages;

  const {data: audits = [], isFetching: isAuditLoading} = useGetAppAccessDeliveryAuditsQuery(selected?.id ?? skipToken);

  useEffect(() => setSelected(undefined), [regionId]);

  useEffect(() => {
    setSelected((current) => workflowPackages.find((item) => item.id === current?.id) ?? workflowPackages[0]);
  }, [workflowPackages]);

  const announce = (event: AppAccessActionEvent) => {
    setNotice(event.message);
    onAction?.(event);
  };

  if (!canView) {
    return (
      <OtsThemeRoot brand={brand} colorScheme={colorScheme} defaultColorScheme="system" className="min-h-full bg-[var(--ots-background)]">
        <AdminPage title="App Access">
          <OtsInlineAlert tone="danger" title="Access denied">You do not have permission to view App Access.</OtsInlineAlert>
        </AdminPage>
      </OtsThemeRoot>
    );
  }

  const openCreate = (mode: AppAccessPackage["mode"], apps: AppAccessTarget["app"][]) => {
    setWorkflow(mode === "ONE_TIME_CREDENTIAL" ? "ONE_PERSON" : "REUSABLE");
    setDraft((current) => ({key: current.key + 1, mode, apps}));
    setShowCreate(true);
    setError(undefined);
  };

  const submitCreate = async (value: AppAccessPackageFormValue) => {
    setError(undefined);
    setNotice(undefined);
    if (!regionId) return setError("Select a region before creating app access.");
    if (value.selectedApps.length === 0) return setError("Select at least one app.");
    if (value.purpose === "INSTALL" && value.mode === "ONE_TIME_CREDENTIAL") return setError("Install-only packages do not contain credentials. Choose a reusable configuration link or change the purpose.");
    const selectedIdentity = appIdentities.find((identity) => identity.id === value.appIdentityId);
    if (value.purpose !== "INSTALL" && !selectedIdentity) return setError("Select an app identity for this region.");
    if (value.purpose !== "INSTALL"
      && value.selectedApps.some((app) => !selectedIdentity?.supportedApps.includes(app))) {
      return setError("The selected app identity does not support every selected app.");
    }

    const request: AppAccessCreatePackage = {
      name: value.name,
      mode: value.mode,
      purpose: value.purpose,
      defaultRegionId: regionId,
      expiresInSeconds: value.mode === "ONE_TIME_CREDENTIAL" ? value.expiresMinutes * 60 : undefined,
      maxResolutions: value.mode === "ONE_TIME_CREDENTIAL" ? 1 : undefined,
      targets: value.selectedApps.map((app) => ({
        app,
        regionId,
        appIdentityId: value.purpose === "INSTALL" ? undefined : selectedIdentity?.id,
      })),
    };
    try {
      const created = await createPackage(request).unwrap();
      setSelected(created);
      setShowCreate(false);
      announce({type: "CREATED", message: "Setup created."});
    } catch (requestError) {
      setError(errorMessage(requestError));
    }
  };

  const copyLink = async (target: AppAccessTarget) => {
    if (!selected) return;
    try {
      const link = await getLink({packageId: selected.id, targetId: target.id}).unwrap();
      await navigator.clipboard.writeText(link.url);
      announce({type: "LINK_COPIED", message: "Setup link copied and recorded in the delivery audit."});
    } catch (requestError) {
      setError(errorMessage(requestError));
    }
  };

  const sendEmail = async (recipients: string[], message?: string): Promise<boolean> => {
    if (!selected) return false;
    setError(undefined);
    try {
      await emailPackage({packageId: selected.id, request: {recipients, message, attachPdf: true}}).unwrap();
      announce({type: "EMAIL_SENT", message: "Email queued through Notifications with the setup PDF attached."});
      return true;
    } catch (requestError) {
      setError(errorMessage(requestError));
      return false;
    }
  };

  const sendSms = async (recipients: string[], message?: string): Promise<boolean> => {
    if (!selected) return false;
    setError(undefined);
    try {
      await smsPackage({packageId: selected.id, request: {recipients, message}}).unwrap();
      announce({type: "SMS_SENT", message: "Setup text queued through Notifications."});
      return true;
    } catch (requestError) {
      setError(errorMessage(requestError));
      return false;
    }
  };

  const exportPdf = async (appAccessPackage: AppAccessPackage) => {
    try {
      const blob = await getPdf(appAccessPackage.id).unwrap();
      downloadBlob(blob, "ots-app-setup.pdf");
      announce({type: "PDF_EXPORTED", message: "Printable setup PDF downloaded."});
    } catch (requestError) {
      setError(errorMessage(requestError));
    }
  };

  const exportQr = async (target: AppAccessTarget) => {
    if (!selected) return;
    try {
      const blob = await getQr({packageId: selected.id, targetId: target.id}).unwrap();
      downloadBlob(blob, `${target.app.toLowerCase()}-setup-qr.png`);
      announce({type: "QR_EXPORTED", message: "Setup QR code downloaded."});
    } catch (requestError) {
      setError(errorMessage(requestError));
    }
  };

  const confirmRevocation = async () => {
    if (!pendingRevocation) return;
    try {
      if (pendingRevocation.kind === "PACKAGE") {
        await revokePackage(pendingRevocation.appAccessPackage.id).unwrap();
        announce({type: "PACKAGE_REVOKED", message: "Setup revoked."});
      } else {
        await revokeTarget({
          packageId: pendingRevocation.target.packageId,
          targetId: pendingRevocation.target.id,
        }).unwrap();
        announce({type: "TARGET_REVOKED", message: "App setup target revoked."});
      }
      setSelected(undefined);
      setPendingRevocation(undefined);
    } catch (requestError) {
      setError(errorMessage(requestError));
    }
  };

  const changeWorkflow = (nextWorkflow: AppAccessWorkflow) => {
    setWorkflow(nextWorkflow);
    setShowCreate(false);
    setError(undefined);
  };

  const toggleCreate = () => {
    if (showCreate) {
      setShowCreate(false);
      return;
    }
    openCreate(
      workflow === "ONE_PERSON" ? "ONE_TIME_CREDENTIAL" : "CONFIGURATION",
      workflow === "ONE_PERSON" ? ["FIELD_GO"] : ["FIELD_GO", "DISPATCH_GO"],
    );
  };

  const refresh = async () => {
    setError(undefined);
    const requests: Promise<unknown>[] = [refetchPackages()];
    if (canView) requests.push(refetchCapabilities());
    if (canManage && regionId) requests.push(refetchIdentities());
    await Promise.all(requests);
    announce({type: "REFRESHED", message: "App Access setups refreshed."});
  };

  return (
    <OtsThemeRoot brand={brand} colorScheme={colorScheme} defaultColorScheme="system" className="min-h-full bg-[var(--ots-background)]">
      <AdminPage
        eyebrow={regionName || "No region selected"}
        title="App Access"
        subtitle="Send a one-person setup or work directly with reusable implementation and onboarding kits."
        actions={<div className="flex gap-2">
          <OtsActionButton icon={<ArrowPathIcon/>} disabled={isFetching} onClick={() => void refresh()}>{isFetching ? "Refreshing" : "Refresh"}</OtsActionButton>
          {canManage && <OtsActionButton variant="primary" icon={<PlusIcon/>} onClick={toggleCreate}>{showCreate ? "Cancel" : workflow === "ONE_PERSON" ? "New individual setup" : "New onboarding kit"}</OtsActionButton>}
        </div>}>
        <div className="mt-6 space-y-5">
          {error && <OtsInlineAlert tone="danger" title="App Access error" onDismiss={() => setError(undefined)}>{error}</OtsInlineAlert>}
          {notice && <OtsInlineAlert tone="success" onDismiss={() => setNotice(undefined)}>{notice}</OtsInlineAlert>}
          {listError && <OtsInlineAlert tone="danger">{errorMessage(listError)}</OtsInlineAlert>}

          <AppAccessWorkflowTabs
            activeWorkflow={workflow}
            onePersonCount={onePersonPackages.length}
            reusableCount={reusablePackages.length}
            onChange={changeWorkflow}/>

          {workflow === "ONE_PERSON" && canManage && (
            <AppAccessQuickStart onSelectApp={(app) => openCreate("ONE_TIME_CREDENTIAL", [app])}/>
          )}

          {showCreate && canManage && (
            <AppAccessPackageForm
              key={draft.key}
              appIdentities={appIdentities.map((identity) => ({
                id: identity.id,
                label: identity.displayName === identity.username
                  ? identity.username
                  : `${identity.displayName} (${identity.username})`,
              }))}
              initialMode={draft.mode}
              initialSelectedApps={draft.apps}
              isSubmitting={createState.isLoading}
              onSubmit={submitCreate}/>
          )}

          <div
            id={`app-access-workflow-${workflow}-panel`}
            role="tabpanel"
            aria-labelledby={`app-access-workflow-${workflow}-tab`}
            className="grid gap-5 lg:grid-cols-[minmax(240px,0.7fr)_minmax(0,1.6fr)]">
            <AppAccessPackageList
              packages={workflowPackages}
              selectedId={selected?.id}
              isLoading={isLoading}
              onSelect={setSelected}
              title={workflow === "ONE_PERSON" ? "Recent one-person setups" : "Reusable onboarding kits"}
              emptyMessage={workflow === "ONE_PERSON"
                ? "No one-person setups have been created for this region."
                : "No reusable onboarding kits are available for this region."}/>
            <AppAccessPackageDetails
              appAccessPackage={selected}
              audits={audits}
              isAuditLoading={isAuditLoading}
              isEmailSending={emailState.isLoading}
              isSmsSending={smsState.isLoading}
              smsDeliveryEnabled={capabilities?.smsDeliveryEnabled === true}
              canManage={canManage}
              canDistributeCredentials={canDistributeCredentials}
              canSendEmail={canSendEmail}
              canSendSms={canSendSms}
              onExportPdf={exportPdf}
              onRevokePackage={(appAccessPackage) => setPendingRevocation({kind: "PACKAGE", appAccessPackage})}
              onCopyTargetLink={copyLink}
              onExportTargetQr={exportQr}
              onRevokeTarget={(target) => setPendingRevocation({kind: "TARGET", target})}
              onSendEmail={sendEmail}
              onSendSms={sendSms}/>
          </div>
        </div>
      </AdminPage>
      <ConfirmationDialog
        open={Boolean(pendingRevocation)}
        tone="danger"
        title={pendingRevocation?.kind === "TARGET"
          ? `Revoke ${getAppAccessAppLabel(pendingRevocation.target.app)} setup?`
          : "Revoke this setup?"}
        description={pendingRevocation?.kind === "TARGET"
          ? "This app link will stop working. Other app links in the setup will remain available. This cannot be undone."
          : "This setup and all of its app links will stop working. This cannot be undone."}
        confirmLabel="Revoke"
        confirmingLabel="Revoking"
        isConfirming={revokePackageState.isLoading || revokeTargetState.isLoading}
        onConfirm={() => void confirmRevocation()}
        onCancel={() => setPendingRevocation(undefined)}
        brand={brand}
        colorScheme="inherit"/>
    </OtsThemeRoot>
  );
};
